import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { LayoutGridIcon, PlusIcon, RadarIcon, ServerIcon, VideoIcon } from "lucide-react";

import { useCameras } from "@/api/cameras";
import { useStatusFeed } from "@/api/status_feed";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { StatusBadge } from "@/components/ui/status-badge";
import { DeviceContextLabel } from "@/components/DeviceContextLabel";
import { FilterBar, ALL } from "@/components/ui/filter-bar";
import { DataTable, type Column } from "@/components/ui/data-table";
import { EmptyState } from "@/components/ui/empty-state";
import { useAuthStore } from "@/store/auth";
import { formatDate } from "@/lib/utils";
import { statusLabel, connectionLabel } from "@/lib/vocab";
import { groupCameras } from "@/lib/groupCameras";
import { nvrKey, nvrFilterOptions } from "@/lib/nvrKey";
import { pageSlice } from "@/lib/pagination";
import type { Camera } from "@/types";
import { AddCameraDialog } from "./AddCameraDialog";
import { DiscoveryModal } from "./DiscoveryModal";

const PAGE_SIZE = 25;

export function CameraListPage() {
  const { data, isLoading, isError, error } = useCameras({ limit: 200 });
  const { statuses, connected } = useStatusFeed();
  const [addOpen, setAddOpen] = useState(false);
  const [discoverOpen, setDiscoverOpen] = useState(false);
  const role = useAuthStore((s) => s.user?.role);
  const canManage = role === "admin";

  // Filters + view state.
  const [search, setSearch] = useState("");
  const [nvr, setNvr] = useState(ALL);
  const [status, setStatus] = useState(ALL);
  const [grouped, setGrouped] = useState(true);
  const [page, setPage] = useState(1);

  const all = data?.items ?? [];

  // Merge the realtime status feed over the fetched status.
  const liveStatus = (c: Camera) => statuses[c.id] ?? c.status;

  // NVR filter options — distinct NVRs present in the data.
  const nvrOptions = useMemo(() => nvrFilterOptions(all), [all]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return all.filter((c) => {
      if (nvr !== ALL && nvrKey(c) !== nvr) return false;
      if (status !== ALL && liveStatus(c) !== status) return false;
      if (q) {
        const hay = [
          c.name,
          c.manufacturer,
          c.model,
          c.nvr_make,
          c.nvr_model,
          c.nvr_serial,
          c.site_name,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [all, search, nvr, status, statuses]);

  const groups = useMemo(() => groupCameras(filtered, "nvr"), [filtered]);

  const statusCol: Column<Camera> = {
    key: "status",
    header: "Status",
    cell: (c) => <StatusBadge status={liveStatus(c)} dot />,
  };

  // Flat-mode columns (Location shows the full hierarchy per row).
  const flatColumns: Column<Camera>[] = [
    {
      key: "name",
      header: "Name",
      cell: (c) => (
        <Link to={`/cameras/${c.id}`} className="font-medium hover:underline">
          {c.name}
        </Link>
      ),
    },
    {
      key: "location",
      header: "Location (Site › NVR › Ch)",
      cell: (c) => <DeviceContextLabel camera={c} className="max-w-[22rem]" />,
    },
    statusCol,
    {
      key: "mode",
      header: "Mode",
      cell: (c) => (
        <span className="text-xs text-muted-foreground">
          {connectionLabel(c.connection_mode)}
        </span>
      ),
    },
    { key: "model", header: "Model", cell: (c) => c.model ?? "—" },
    {
      key: "last_seen",
      header: "Last seen",
      cell: (c) => (
        <span className="text-muted-foreground">{formatDate(c.last_seen_at)}</span>
      ),
    },
  ];

  // Per-group columns (NVR is the section header, so no Location column).
  const groupColumns: Column<Camera>[] = [
    {
      key: "channel",
      header: "Ch",
      align: "right",
      className: "w-16",
      cell: (c) => (
        <span className="tabular-nums text-muted-foreground">
          {c.channel_no ?? "—"}
        </span>
      ),
    },
    {
      key: "name",
      header: "Name",
      cell: (c) => (
        <Link to={`/cameras/${c.id}`} className="font-medium hover:underline">
          {c.name}
        </Link>
      ),
    },
    statusCol,
    { key: "model", header: "Model", cell: (c) => c.model ?? "—" },
    {
      key: "last_seen",
      header: "Last seen",
      cell: (c) => (
        <span className="text-muted-foreground">{formatDate(c.last_seen_at)}</span>
      ),
    },
  ];

  const anyFilter = search !== "" || nvr !== ALL || status !== ALL;
  const clearFilters = () => {
    setSearch("");
    setNvr(ALL);
    setStatus(ALL);
    setPage(1);
  };

  const actions = canManage ? (
    <>
      <Button variant="outline" onClick={() => setDiscoverOpen(true)}>
        <RadarIcon className="mr-2 h-4 w-4" />
        Discover on LAN
      </Button>
      <Button onClick={() => setAddOpen(true)}>
        <PlusIcon className="mr-2 h-4 w-4" />
        Add camera
      </Button>
    </>
  ) : null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Cameras</h1>
          <p className="text-sm text-muted-foreground">
            {data ? `${data.total} registered` : "Loading…"}
            {" · "}
            {connected ? (
              <span className="text-emerald-600">● live</span>
            ) : (
              <span>○ connecting</span>
            )}
          </p>
        </div>
        <div className="flex gap-2">
          <div className="flex overflow-hidden rounded-md border">
            <button
              className={
                "px-3 py-1.5 text-xs " +
                (grouped ? "bg-muted font-medium" : "text-muted-foreground")
              }
              onClick={() => setGrouped(true)}
            >
              <ServerIcon className="mr-1 inline h-3.5 w-3.5" />
              By NVR
            </button>
            <button
              className={
                "px-3 py-1.5 text-xs " +
                (!grouped ? "bg-muted font-medium" : "text-muted-foreground")
              }
              onClick={() => setGrouped(false)}
            >
              Flat list
            </button>
          </div>
          <Button asChild variant="outline">
            <Link to="/grid">
              <LayoutGridIcon className="mr-2 h-4 w-4" />
              Grid
            </Link>
          </Button>
        </div>
      </div>

      <FilterBar
        search={search}
        onSearchChange={(v) => {
          setSearch(v);
          setPage(1);
        }}
        searchPlaceholder="Search name, model, NVR, site…"
        filters={[
          {
            key: "nvr",
            placeholder: "All NVRs",
            value: nvr,
            onChange: (v) => {
              setNvr(v);
              setPage(1);
            },
            options: nvrOptions,
          },
          {
            key: "status",
            placeholder: "All statuses",
            value: status,
            onChange: (v) => {
              setStatus(v);
              setPage(1);
            },
            options: (["online", "offline", "pending", "unprovisioned", "error"] as const).map(
              (s) => ({ value: s, label: statusLabel(s) }),
            ),
          },
        ]}
        resultLabel={anyFilter ? `${filtered.length} of ${all.length}` : undefined}
        onClear={anyFilter ? clearFilters : undefined}
        actions={actions}
      />

      {isError && (
        <Card className="border-destructive/40">
          <CardContent className="py-10 text-center text-sm text-destructive">
            Could not load cameras: {(error as Error).message}
          </CardContent>
        </Card>
      )}

      {!isError && grouped ? (
        // ── Grouped by NVR ──────────────────────────────────────────────
        isLoading ? (
          <Card>
            <CardContent className="py-10 text-center text-sm text-muted-foreground">
              Loading cameras…
            </CardContent>
          </Card>
        ) : groups.length === 0 ? (
          <EmptyState
            icon={VideoIcon}
            title={anyFilter ? "No cameras match these filters" : "No cameras yet"}
            description={
              anyFilter
                ? "Try clearing the search or filters."
                : canManage
                  ? "Use Discover on LAN to find ONVIF devices, or add one manually."
                  : undefined
            }
            action={
              anyFilter ? (
                <Button variant="outline" size="sm" onClick={clearFilters}>
                  Clear filters
                </Button>
              ) : undefined
            }
          />
        ) : (
          <div className="space-y-5">
            {groups.map((g) => {
              const onlineN = g.cameras.filter(
                (c) => liveStatus(c) === "online",
              ).length;
              const sample = g.cameras[0];
              return (
                <div key={g.key} className="space-y-2">
                  <div className="flex items-center gap-3">
                    <ServerIcon className="h-4 w-4 text-muted-foreground" />
                    <span className="font-semibold">{g.label}</span>
                    {sample?.site_name && (
                      <span className="text-xs text-muted-foreground">
                        {sample.site_name}
                      </span>
                    )}
                    <StatusBadge
                      status={onlineN === g.cameras.length ? "online" : "warning"}
                      label={`${onlineN}/${g.cameras.length} online`}
                    />
                  </div>
                  <DataTable
                    columns={groupColumns}
                    rows={g.cameras}
                    rowKey={(c) => c.id}
                    density="compact"
                  />
                </div>
              );
            })}
          </div>
        )
      ) : null}

      {!isError && !grouped && (
        // ── Flat list (sortable-ready, paginated) ───────────────────────
        <DataTable
          columns={flatColumns}
          rows={pageSlice(filtered, page, PAGE_SIZE)}
          rowKey={(c) => c.id}
          isLoading={isLoading}
          density="compact"
          onRowClick={undefined}
          emptyIcon={VideoIcon}
          emptyTitle={anyFilter ? "No cameras match these filters" : "No cameras yet"}
          emptyDescription={
            anyFilter ? "Try clearing the search or filters." : undefined
          }
          pagination={{
            total: filtered.length,
            page,
            pageSize: PAGE_SIZE,
            onPageChange: setPage,
          }}
        />
      )}

      {canManage && (
        <>
          <AddCameraDialog open={addOpen} onOpenChange={setAddOpen} />
          <DiscoveryModal open={discoverOpen} onOpenChange={setDiscoverOpen} />
        </>
      )}
    </div>
  );
}
